import React, { useMemo, useState } from 'react';
import {
  X, Download, ArrowUp, ArrowDown, RotateCcw, Save, Eye, GripVertical,
} from 'lucide-react';
import { COLUMNS, COLUMN_BY_ID, DATE_FORMATS, BOOL_FORMATS, STATUS_FORMATS, DEFAULT_EXPORT_COLUMNS } from '../columns';
import { DELIMITERS, DEFAULT_EXPORT_OPTIONS, buildCsv, buildFilename } from '../csv';

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-medium text-slate-400 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
  </div>
);

const selectClass = `w-full bg-slate-900 border border-slate-600 text-slate-200 rounded-md px-3 py-2 text-sm
  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`;

const Toggle = ({ checked, onChange, label, hint }) => (
  <label className="flex items-start gap-2.5 p-2 rounded hover:bg-slate-700/40 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 rounded text-blue-500 focus:ring-blue-500 bg-slate-700 border-slate-500"
    />
    <span>
      <span className="block text-sm text-slate-200">{label}</span>
      {hint && <span className="block text-[11px] text-slate-500">{hint}</span>}
    </span>
  </label>
);

/**
 * `selection` is the ordered list the user is building:
 *   [{ id, header }]  — header is their own label, defaulting to the column's.
 * Order in this list is the order of columns in the file.
 */
const ExportModal = ({
  filteredRows, allRows, startDate, endDate, filterSummary, onClose, onExported,
  initialSelection, initialOptions, initialScope, onPersist,
}) => {
  const [selection, setSelection] = useState(initialSelection);
  const [options, setOptions] = useState(initialOptions);
  const [scope, setScope] = useState(initialScope || 'filtered');
  const [tab, setTab] = useState('columns');
  const [saved, setSaved] = useState(false);

  const setOption = (patch) => { setOptions((o) => ({ ...o, ...patch })); setSaved(false); };

  const selectedIds = useMemo(() => new Set(selection.map((s) => s.id)), [selection]);
  const exportRows = scope === 'filtered' ? filteredRows : allRows;

  const toggleColumn = (id) => {
    setSaved(false);
    setSelection((sel) => sel.some((s) => s.id === id)
      ? sel.filter((s) => s.id !== id)
      // New columns append in the registry's own order position-independently:
      // they go on the end, which is where a user who just ticked one expects it.
      : [...sel, { id, header: COLUMN_BY_ID[id].label }]);
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= selection.length) return;
    setSaved(false);
    setSelection((sel) => {
      const next = [...sel];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const renameColumn = (id, header) => {
    setSaved(false);
    setSelection((sel) => sel.map((s) => (s.id === id ? { ...s, header } : s)));
  };

  const resetAll = () => {
    setSelection(DEFAULT_EXPORT_COLUMNS.map((id) => ({ id, header: COLUMN_BY_ID[id].label })));
    setOptions(DEFAULT_EXPORT_OPTIONS);
    setSaved(false);
  };

  const preview = useMemo(() => {
    if (!selection.length) return '';
    const sample = exportRows.slice(0, 4);
    // The BOM is invisible but would still render as a stray glyph in <pre>.
    return buildCsv(sample, selection, { ...options, includeBom: false });
  }, [selection, options, exportRows]);

  const filename = buildFilename(options.filenameTemplate, { startDate, endDate });

  // Downloading also commits the configuration, so Quick export afterwards
  // repeats exactly what was just exported.
  const handleExport = () => {
    const csv = buildCsv(exportRows, selection, options);
    onExported(csv, filename, exportRows.length, { selection, options }, scope);
  };

  const handleSavePreset = () => {
    onPersist({ selection, options }, scope);
    setSaved(true);
  };

  const TabButton = ({ id, children }) => (
    <button
      onClick={() => setTab(id)}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        tab === id
          ? 'border-blue-500 text-blue-400'
          : 'border-transparent text-slate-400 hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-700">
          <div>
            <h3 className="text-lg font-semibold text-slate-100">Export to CSV</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {selection.length} column{selection.length === 1 ? '' : 's'} ·{' '}
              {exportRows.length.toLocaleString()} row{exportRows.length === 1 ? '' : 's'} · {filename}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-1 px-4 border-b border-slate-700">
          <TabButton id="columns">Columns &amp; order</TabButton>
          <TabButton id="format">Formatting</TabButton>
          <TabButton id="preview">Preview</TabButton>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'columns' && (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-2">Available columns</h4>
                <p className="text-[11px] text-slate-500 mb-3">
                  Tick a column to add it to the end of the export.
                </p>
                <div className="border border-slate-700 rounded-lg p-2 max-h-[22rem] overflow-y-auto">
                  {COLUMNS.map((col) => (
                    <label
                      key={col.id}
                      className="flex items-center gap-2.5 p-2 rounded hover:bg-slate-700/40 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(col.id)}
                        onChange={() => toggleColumn(col.id)}
                        className="rounded text-blue-500 focus:ring-blue-500 bg-slate-700 border-slate-500"
                      />
                      <span className="text-sm text-slate-200 flex-1">{col.label}</span>
                      <span className="text-[11px] text-slate-500">{col.type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-2">Export order &amp; headings</h4>
                <p className="text-[11px] text-slate-500 mb-3">
                  Columns appear in the file top to bottom. Edit any heading to rename it.
                </p>
                <div className="border border-slate-700 rounded-lg p-2 max-h-[22rem] overflow-y-auto space-y-1">
                  {selection.length === 0 && (
                    <p className="text-sm text-slate-500 p-4 text-center">
                      No columns selected yet.
                    </p>
                  )}
                  {selection.map((item, index) => (
                    <div key={item.id} className="flex items-center gap-1.5 p-1.5 bg-slate-900/50 rounded">
                      <GripVertical className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                      <span className="text-[11px] text-slate-500 w-5 flex-shrink-0">{index + 1}</span>
                      <input
                        type="text"
                        value={item.header}
                        onChange={(e) => renameColumn(item.id, e.target.value)}
                        title={COLUMN_BY_ID[item.id]?.label}
                        className="flex-1 min-w-0 bg-slate-900 border border-slate-700 text-slate-200 rounded px-2 py-1 text-xs
                                   focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => move(index, -1)}
                        disabled={index === 0}
                        className="p-1 text-slate-400 hover:text-slate-200 disabled:opacity-30"
                        aria-label={`Move ${item.header} up`}
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => move(index, 1)}
                        disabled={index === selection.length - 1}
                        className="p-1 text-slate-400 hover:text-slate-200 disabled:opacity-30"
                        aria-label={`Move ${item.header} down`}
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => toggleColumn(item.id)}
                        className="p-1 text-slate-500 hover:text-red-400"
                        aria-label={`Remove ${item.header}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'format' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Rows to export">
                  <select value={scope} onChange={(e) => setScope(e.target.value)} className={selectClass}>
                    <option value="filtered">
                      Filtered rows — what the table shows ({filteredRows.length.toLocaleString()})
                    </option>
                    <option value="all">
                      Everything in the date range ({allRows.length.toLocaleString()})
                    </option>
                  </select>
                  {scope === 'filtered' && filterSummary && (
                    <p className="text-[11px] text-slate-500 mt-1">Active filters: {filterSummary}</p>
                  )}
                </Field>

                <Field label="File name" hint="{start}, {end} and {today} are replaced. .csv is added for you.">
                  <input
                    type="text"
                    value={options.filenameTemplate}
                    onChange={(e) => setOption({ filenameTemplate: e.target.value })}
                    className={selectClass}
                  />
                </Field>

                <Field label="Date & time format">
                  <select
                    value={options.dateFormat}
                    onChange={(e) => setOption({ dateFormat: e.target.value })}
                    className={selectClass}
                  >
                    {DATE_FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                </Field>

                <Field label="Status values">
                  <select
                    value={options.statusFormat}
                    onChange={(e) => setOption({ statusFormat: e.target.value })}
                    className={selectClass}
                  >
                    {STATUS_FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                </Field>

                <Field label="Yes/no values">
                  <select
                    value={options.boolFormat}
                    onChange={(e) => setOption({ boolFormat: e.target.value })}
                    className={selectClass}
                  >
                    {BOOL_FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                </Field>

                <Field label="Column separator">
                  <select
                    value={options.delimiter}
                    onChange={(e) => setOption({ delimiter: e.target.value })}
                    className={selectClass}
                  >
                    {DELIMITERS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                </Field>

                <Field
                  label="Empty values appear as"
                  hint="Left blank, empty cells stay truly empty — which is what Excel and Sheets expect."
                >
                  <input
                    type="text"
                    value={options.nullText}
                    onChange={(e) => setOption({ nullText: e.target.value })}
                    placeholder="(blank)"
                    className={selectClass}
                  />
                </Field>

                <Field label="Quoting">
                  <select
                    value={options.quoteMode}
                    onChange={(e) => setOption({ quoteMode: e.target.value })}
                    className={selectClass}
                  >
                    <option value="minimal">Only when needed</option>
                    <option value="always">Quote every field</option>
                  </select>
                </Field>
              </div>

              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-sm font-medium text-slate-300 mb-2">Spreadsheet compatibility</h4>
                <div className="grid md:grid-cols-2 gap-1">
                  <Toggle
                    checked={options.includeHeader}
                    onChange={(v) => setOption({ includeHeader: v })}
                    label="Include a header row"
                  />
                  <Toggle
                    checked={options.includeBom}
                    onChange={(v) => setOption({ includeBom: v })}
                    label="Add UTF-8 BOM"
                    hint="Keep this on or Excel garbles accented characters."
                  />
                  <Toggle
                    checked={options.forceTextTracking}
                    onChange={(v) => setOption({ forceTextTracking: v })}
                    label="Force tracking numbers to text"
                    hint={'Writes ="877233003102" so Excel stops showing 8.77233E+11.'}
                  />
                  <Toggle
                    checked={options.lineEnding === 'crlf'}
                    onChange={(v) => setOption({ lineEnding: v ? 'crlf' : 'lf' })}
                    label="Windows line endings (CRLF)"
                    hint="Turn off for Unix tooling."
                  />
                </div>
              </div>
            </div>
          )}

          {tab === 'preview' && (
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-300 mb-2">
                <Eye className="w-4 h-4" />
                <span>First {Math.min(4, exportRows.length)} of {exportRows.length.toLocaleString()} rows, exactly as written</span>
              </div>
              <pre className="bg-slate-900 border border-slate-700 rounded-lg p-4 text-xs text-slate-300
                              overflow-x-auto whitespace-pre max-h-[24rem]">
                {preview || 'Select at least one column to preview the file.'}
              </pre>
              {options.includeBom && (
                <p className="text-[11px] text-slate-500 mt-2">
                  The downloaded file also starts with an invisible UTF-8 BOM, which is not shown here.
                </p>
              )}
            </div>
          )}
        </div>

        <p className="px-6 pt-3 text-[11px] text-slate-500">
          These settings are saved to your account and follow you to any browser.
          Downloading also saves them, so <strong className="text-slate-400">Quick export</strong> repeats this exact file.
        </p>

        <div className="flex flex-wrap justify-between items-center gap-3 px-6 py-4 border-t border-slate-700 bg-slate-800/80">
          <div className="flex gap-2">
            <button
              onClick={resetAll}
              className="px-3 py-2 rounded-md text-sm bg-slate-700 text-slate-300 hover:bg-slate-600 flex items-center gap-1.5"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={handleSavePreset}
              disabled={selection.length === 0}
              className="px-3 py-2 rounded-md text-sm bg-slate-700 text-slate-300 hover:bg-slate-600
                         disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              title="Save to your account without downloading — Quick export and Copy use these too"
            >
              <Save className="w-4 h-4" />
              {saved ? 'Saved' : 'Save without exporting'}
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm bg-slate-700 text-slate-300 hover:bg-slate-600"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={selection.length === 0 || exportRows.length === 0}
              className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${
                selection.length === 0 || exportRows.length === 0
                  ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <Download className="w-4 h-4" />
              Download CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
